import React, {
  useCallback,
  useEffect,
  useMemo,
  useRef,
  useState,
} from 'react';
import { createPortal } from 'react-dom';
import { ConfigProvider } from 'antd';
import { isApiError, isRequestCancelled } from '@/apis/apiErrors';
import { EllipsisTooltip } from '@/components';
import {
  CreateAIChatFeedback,
  GetAIChatSession,
  GetAIChatSessions,
  SendAIChatMessage,
  StartAIChatSession,
  UpdateAIChatFeedback,
} from '@/apis/aiChat';
import {
  CreateAgentCoreChatFeedback,
  GetAgentCoreChatDrilldown,
  GetAgentCoreChatSession,
  GetAgentCoreChatSessions,
  StartAgentCoreChatSession,
  StreamAgentCoreChatMessage,
  UpdateAgentCoreChatFeedback,
} from '@/apis/agentcoreChat';
import type { AgentCoreChatDrilldownResult } from '@/apis/agentcoreChat';
import type { ChatChartDrilldown } from '@/common/chat/parseChatMessageParts';
import { useToken } from '@/common/helper';
import { useInsightChatHandoff } from '@/layout/AppHelpCenter/hooks';
import type { HelpCenterAnswerSource } from '@/layout/AppHelpCenter/types';
import type {
  AIChatDrawerProps,
  AIChatFeedbackRating,
  AIChatMessage,
  AIChatMessagePart,
  AIChatSession,
  AIChatThinkingStep,
  AskUserAnswers,
  AskUserRequest,
} from './types';
import {
  AIChatChevronLeftIcon,
  AIChatCollapseIcon,
  AIChatEditSquareIcon,
  AIChatExpandIcon,
  AIChatExpandMoreIcon,
  AIChatSearchIcon,
  CloseDrawerIcon,
} from '@/assets/icons/icons';
import { AI_CHAT_SUGGESTED_QUESTIONS } from './constants';
import AIChatThread from './components/AIChatThread';
import AIChatChartDrilldown from './components/AIChatChartDrilldown';
import AIChatQuestionPanel from './components/AIChatQuestionPanel';
import { isInAppPath, summarizeAskUserAnswers } from './chatActions';
import { GoogleMapsProvider } from '@/components/GoogleMapsProvider';
import ShipmentDetailDrawerWithTeams from '@/pages/Shipments/ShipmentDetailDrawerWithTeams';
import ShipmentNotificationDrawer from '@/pages/Shipments/ShipmentNotifications/ShipmentNotificationDrawer';
import EditShipmentDrawerWithTeams from '@/pages/Shipments/EditShipmentDrawerWithTeams';
import QuoteShipmentDrawer from '@/pages/Shipments/QuoteShipmentDrawer/QuoteShipmentDrawer';
import CommonDrawer from '@/components/CommonDrawer';
import type { SingleShipmentDataExtended } from '@/apis/types';
import { useMenuPermissions } from '@/hooks';
import { MenuLabels } from '@/common/enum';
import { THOUGHT_STEP_NAME } from './components/AIChatThinkingTimeline';
import {
  AIChatComposer,
  AIChatHistoryGroups,
  AIChatHistorySearch,
  AIChatSavedQuestionsSection,
  AIChatSuggestedQuestionsSection,
} from './components/parts';
import {
  useAIChatSavedQuestions,
  useCopiedActions,
} from './hooks/useAIChatDrawerHooks';
import {
  appendTypingIfPending,
  chunkItems,
  clearStoredLastSessionId,
  conversationHistoryToMessages,
  customResponseErrorMessage,
  EXPANDED_VIEW_QUERY,
  getStoredLastSessionId,
  getSessionsFromPayload,
  getSessionsPageCountFromPayload,
  groupSessionsByCreatedAt,
  normalizeSql,
  storeLastSessionId,
  toHelpSource,
} from './utils';
import './AIChatDrawer.css';

const AI_CHAT_HISTORY_PAGE_SIZE = 20;
const AI_CHAT_HISTORY_SCROLL_THRESHOLD = 48;
const AI_CHAT_BODY_SCROLL_LOCK_CLASS = 'zoomlogi-ai-chat-drawer-scroll-lock';
// A sessions-list fetch that resolves before this elapses never shows
// "Loading chats..." at all. One that does show it stays visible at least
// HISTORY_LOADING_MIN_VISIBLE_MS even if the data arrives sooner, so it
// never flashes off again either. Centralized here so every call site
// (opening the dropdown, typing a search, scrolling for more, the
// mount-time check) times the indicator the same way instead of each
// re-deciding when "loading" should actually be visible.
const HISTORY_LOADING_DELAY_MS = 150;
const HISTORY_LOADING_MIN_VISIBLE_MS = 300;
// A background refresh triggered just by reopening the dropdown is skipped
// in favor of an immediate refetch once the last successful fetch is this
// old -- no reason to wait out the normal debounce for data that's
// definitely stale.
const HISTORY_STALE_AFTER_MS = 30_000;

function formatConversationTitle(question: string): string {
  return question.length > 48 ? `${question.slice(0, 48)}...` : question;
}

// Resolves as soon as `run()` itself finishes -- the trailing
// minimum-visible-duration hide (if the indicator ever showed) is scheduled
// separately and does NOT delay the returned promise. It only affects a
// purely cosmetic setState; nothing that gates a mutex or a queued retry
// should ever have to wait on it.
function withDelayedLoadingIndicator<T>(
  setLoading: (value: boolean) => void,
  run: () => Promise<T>
): Promise<T> {
  let shown = false;
  let shownAt = 0;
  const showTimer = window.setTimeout(() => {
    shown = true;
    shownAt = Date.now();
    setLoading(true);
  }, HISTORY_LOADING_DELAY_MS);

  const hide = () => {
    window.clearTimeout(showTimer);
    if (!shown) return;
    const remaining = HISTORY_LOADING_MIN_VISIBLE_MS - (Date.now() - shownAt);
    if (remaining > 0) {
      window.setTimeout(() => setLoading(false), remaining);
    } else {
      setLoading(false);
    }
  };

  return run().then(
    (value) => {
      hide();
      return value;
    },
    (error: unknown) => {
      hide();
      throw error;
    }
  );
}

function sessionsListsEqual(a: AIChatSession[], b: AIChatSession[]): boolean {
  if (a.length !== b.length) return false;
  return a.every(
    (session, index) =>
      session.id === b[index].id &&
      session.title === b[index].title &&
      session.createdAt === b[index].createdAt
  );
}

const AIChatDrawer: React.FC<AIChatDrawerProps> = ({
  open,
  onClose,
  pendingQuestion,
  onPendingQuestionSent,
  onOpenLink,
}) => {
  const { decodedUserToken } = useToken();
  // Feature gate for the new AgentCore-based agent — granted via Roles &
  // Permissions (same "AI Insights chatbot" view permission as the old
  // chatbot, see agentcore_chat/permissions.py). Backend independently
  // re-checks this on every request; this claim only decides which UI
  // path to take.
  const useAgentCoreChatbot = decodedUserToken()?.agentcore_chatbot === true;
  const [question, setQuestion] = useState('');
  const [sessions, setSessions] = useState<AIChatSession[]>([]);
  const [messages, setMessages] = useState<AIChatMessage[]>([]);
  const [sessionId, setSessionId] = useState<string | null>(null);
  // True only while genuinely uncertain whether to restore an existing
  // session or show the welcome screen (the mount-effect restore-check).
  // Once that's decided -- either way -- this goes false immediately,
  // even if a network call (session creation) is still in flight. Actions
  // that already know the answer (e.g. "New chat") never set this at all.
  //
  // Seeded from sessionStorage (not a plain `false`) because this
  // component mounts once, permanently, as part of the header -- long
  // before the drawer is ever opened -- and stays mounted across
  // open/close. The mount-effect below is what actually kicks off the
  // restore check and flips this, but that effect only runs *after* the
  // first paint following `open` turning true. Starting from `false`
  // would paint the welcome screen for that one frame whenever a stored
  // id exists, then flip to the loading state, then flip again to the
  // restored conversation -- a visible flicker even though no redundant
  // session ever gets created. Reading storage directly here closes that
  // gap: the first paint already reflects "there's something to check".
  const [determiningSession, setDeterminingSession] = useState(
    () => getStoredLastSessionId() != null
  );
  const [transcriptLoadingSessionId, setTranscriptLoadingSessionId] = useState<
    string | null
  >(null);
  const [pendingAssistantBySession, setPendingAssistantBySession] = useState(
    () => new Set<string>()
  );
  // The `ask_user` request shown as the question panel at the end of the thread,
  // tied to the session whose turn raised it (so switching chats and back
  // still finds it). Cleared by sending any message -- answers or a typed
  // message, the latter implicitly cancelling it -- by dismissing it, or by
  // starting a new chat.
  const [openAskUser, setOpenAskUser] = useState<{
    sessionId: string;
    request: AskUserRequest;
  } | null>(null);
  const [chatSwitcherOpen, setChatSwitcherOpen] = useState(false);
  const [chatSearch, setChatSearch] = useState('');
  // Swaps the drawer's body from the chat thread to a shipment/alert list --
  // same container as the thread, not an overlay on top of it, matching how
  // this drawer already swaps between its other full-body views. Cleared
  // (back to null) to return to the thread.
  const [chartDrilldown, setChartDrilldown] = useState<{
    category: string;
    entity: ChatChartDrilldown['entity'];
    filters: ChatChartDrilldown['filters'];
    loading: boolean;
    error: string | null;
    data: AgentCoreChatDrilldownResult | null;
  } | null>(null);
  // Swaps the drawer's body again, on top of the drilldown list, when a
  // shipment/alert row is clicked -- reuses the same detail drawer the rest
  // of the app uses (Home, Control Tower), including its own JWT-authenticated
  // data fetching. Cleared (back to null) to return to the drilldown list.
  const [openShipmentDetailId, setOpenShipmentDetailId] = useState<
    string | null
  >(null);
  // Swaps the body again, one level deeper than the shipment detail view,
  // when its notification bell is clicked. ShipmentNotificationDrawer has
  // its own internal header/back-to-detail control (built expecting this
  // exact nesting), so unlike the detail view we don't add our own extra
  // back button on top of it here.
  const [notificationsShipmentId, setNotificationsShipmentId] = useState<
    string | null
  >(null);
  // Swaps in over the currently-open shipment (same slot ShipmentDetailDrawer
  // WithTeams renders in, matching HomeChartShipmentsList's own Detail/Edit
  // pattern) when its "..." menu's Edit shipment item is clicked. Always
  // refers to whichever shipment openShipmentDetailId already names, so no
  // separate id needs tracking here.
  const [isEditingShipment, setIsEditingShipment] = useState(false);
  // Generate return label opens as its own overlay on top of whatever's
  // currently showing (same as the real Shipments pages -- see
  // NewShipments.tsx's handleGenerateReturnLabel), rather than replacing the
  // shipment detail view the way Edit does.
  const [returnLabelShipment, setReturnLabelShipment] =
    useState<SingleShipmentDataExtended | null>(null);
  const shipmentMenuPermissions = useMenuPermissions(MenuLabels.Shipment);
  // Which way the *next* view-swap animation should slide in from --
  // 'forward' (drilling deeper: chat -> list -> detail) enters from the
  // right, 'backward' (a back button) enters from the left. Set right
  // before the state change that actually swaps the view.
  const [navDirection, setNavDirection] = useState<'forward' | 'backward'>(
    'forward'
  );
  const viewEnterClass =
    navDirection === 'forward'
      ? 'ai-chat-drawer__view-enter--forward'
      : 'ai-chat-drawer__view-enter--backward';
  const [expandedSearchOpen, setExpandedSearchOpen] = useState(false);
  const [expandedSearch, setExpandedSearch] = useState('');
  const [historyHasMore, setHistoryHasMore] = useState(false);
  const [historyLoading, setHistoryLoading] = useState(false);
  const historySearchRef = useRef('');
  const historyPageRef = useRef(1);
  const historyHasMoreRef = useRef(false);
  const historyLoadingRef = useRef(false);
  // A search/refresh request that arrives while one is already in flight is
  // remembered here (overwriting whatever was queued before) instead of
  // being dropped -- loadSessionHistory fires it the moment the in-flight
  // one finishes, so typing quickly can never leave the list stuck showing
  // results for an older search term.
  const pendingHistoryRequestRef = useRef<{
    search: string;
    page: number;
    append: boolean;
  } | null>(null);
  const lastHistoryFetchAtRef = useRef(0);
  const [isExpanded, setIsExpanded] = useState(false);
  const [expandedSidebarCollapsed, setExpandedSidebarCollapsed] =
    useState(false);
  const [activeMessageActions, setActiveMessageActions] = useState<
    Record<string, string>
  >({});
  const [openQueryMessages, setOpenQueryMessages] = useState<
    Record<string, boolean>
  >({});
  const [activeChatTitle, setActiveChatTitle] = useState('New chat');
  const { copiedActionKeys, copyText, clearCopiedActions } = useCopiedActions();
  // Saved questions only exist on the old ai_insights_chatbot API — there's
  // no separate AgentCore-backed endpoint, and none is needed: AgentCore
  // access now requires the same ai_insights_chatbot permission (see
  // is_user_allowed_for_agentcore_chat), so any AgentCore-mode user is
  // guaranteed to be authorized for this endpoint too.
  const {
    savedQuestions,
    savingQuestion,
    showAllSaved,
    setShowAllSaved,
    showAllExpandedSaved,
    setShowAllExpandedSaved,
    isCurrentQuestionSaved,
    visibleSavedRows,
    expandedSavedRows,
    toggleSaveQuestion,
    removeSavedQuestion,
  } = useAIChatSavedQuestions(open, question);
  const textareaRef = useRef<HTMLTextAreaElement>(null);
  const threadRef = useRef<HTMLElement>(null);
  const bodyRef = useRef<HTMLElement>(null);
  const conversationButtonRef = useRef<HTMLButtonElement>(null);
  const chatSwitcherRef = useRef<HTMLDivElement>(null);
  const sessionInitialisedRef = useRef(false);
  const sessionIdRef = useRef<string | null>(null);
  const selectSessionAbortRef = useRef<AbortController | null>(null);
  const sessionDataVersionRef = useRef<Record<string, number>>({});
  const pendingAssistantBySessionRef = useRef<Set<string>>(
    pendingAssistantBySession
  );

  // sessionIdRef must be correct the instant sessionId is set, not a tick
  // later -- code right after setSessionId (e.g. an async fetch's guard
  // check) can run before React commits and flushes effects, and a
  // useEffect-based sync loses that race under fast-enough responses
  // (reliably in tests; rarely but really over a real network too).
  const setActiveSessionId = useCallback((id: string | null) => {
    sessionIdRef.current = id;
    storeLastSessionId(id);
    setSessionId(id);
  }, []);

  const hasMessages = messages.length > 0;
  const visibleTranscriptLoadingSessionId =
    transcriptLoadingSessionId ??
    (determiningSession ? '__initial-session__' : null);
  const hasActiveConversation =
    hasMessages || visibleTranscriptLoadingSessionId !== null;
  const currentSessionAwaitingReply =
    sessionId != null && pendingAssistantBySession.has(sessionId);
  const sendDisabled =
    !question.trim() || !sessionId || currentSessionAwaitingReply;

  useEffect(() => {
    if (!open) return;
    const timeout = window.setTimeout(() => textareaRef.current?.focus(), 80);
    return () => window.clearTimeout(timeout);
  }, [open]);

  useEffect(() => {
    if (!open) return;
    const root = document.documentElement;
    // Hiding the scrollbar frees up the width it occupied, which would
    // otherwise shift the whole layout sideways; compensate by reserving
    // that same width as padding.
    const scrollbarWidth = window.innerWidth - root.clientWidth;
    root.classList.add(AI_CHAT_BODY_SCROLL_LOCK_CLASS);
    root.style.paddingRight = `${scrollbarWidth}px`;
    return () => {
      root.classList.remove(AI_CHAT_BODY_SCROLL_LOCK_CLASS);
      root.style.paddingRight = '';
    };
  }, [open]);

  useEffect(() => {
    pendingAssistantBySessionRef.current = pendingAssistantBySession;
  }, [pendingAssistantBySession]);

  const lastMessageContentLength =
    messages[messages.length - 1]?.content?.length;
  useEffect(() => {
    if (!threadRef.current) return;
    threadRef.current.scrollTop = threadRef.current.scrollHeight;
    // Keyed on count + last message's length, not the messages array itself,
    // so an in-place edit to an existing message (e.g. a feedback click)
    // doesn't yank the scroll position — only a new message or the
    // in-progress streamed answer growing should do that.
  }, [messages.length, lastMessageContentLength]);

  // The drilldown list and shipment detail have no scroll container of
  // their own (see AIChatDrawer.css) -- they scroll via .ai-chat-drawer__body
  // itself, the same element every other view shares. Without this, whatever
  // scroll position the chat thread was left at (or a previous drilldown/
  // detail screen) carries straight over into the newly-mounted view,
  // potentially landing it scrolled past its own back button/title.
  useEffect(() => {
    if (!bodyRef.current) return;
    bodyRef.current.scrollTop = 0;
    // Category/entity, not the chartDrilldown object itself -- that
    // reference also changes when its data finishes loading (same view,
    // not a swap), which would otherwise yank the user's scroll position
    // back to the top mid-read.
  }, [chartDrilldown?.category, chartDrilldown?.entity, openShipmentDetailId]);

  useEffect(() => {
    if (open) return;
    // Deliberately UI-only: closing the drawer must never touch session,
    // message, or sessions-list state (nor abort an in-flight transcript
    // fetch) -- doing so is what caused reopening to briefly flash a
    // "New chat"/welcome state before repainting whatever was already
    // correct. The drawer isn't visible while closed, so leaving that
    // state alone is harmless, and it means reopening within the same
    // tab needs no fetch and no intermediate render at all: whatever was
    // on screen when you closed it is still there. Any staleness (a new
    // message that arrived elsewhere) is refreshed quietly by the
    // existing session-select/transcript logic, not by resetting first.
    clearCopiedActions();
    setIsExpanded(false);
    setChatSwitcherOpen(false);
    setExpandedSidebarCollapsed(false);
    setExpandedSearchOpen(false);
    setExpandedSearch('');
    setChatSearch('');
    setHistoryHasMore(false);
    setHistoryLoading(false);
    historySearchRef.current = '';
    historyPageRef.current = 1;
    historyHasMoreRef.current = false;
    historyLoadingRef.current = false;
    setQuestion('');
  }, [clearCopiedActions, open]);

  useEffect(
    () => () => {
      selectSessionAbortRef.current?.abort();
    },
    []
  );

  useEffect(() => {
    if (!open || typeof window === 'undefined') return;

    const media = window.matchMedia(EXPANDED_VIEW_QUERY);
    const handleViewportChange = (event: MediaQueryListEvent) => {
      if (!event.matches) {
        setIsExpanded(false);
        setExpandedSidebarCollapsed(false);
      }
    };

    if (!media.matches) {
      setIsExpanded(false);
      setExpandedSidebarCollapsed(false);
    }

    media.addEventListener('change', handleViewportChange);
    return () => media.removeEventListener('change', handleViewportChange);
  }, [open]);

  useEffect(() => {
    if (!open || !chatSwitcherOpen) return;

    const handleOutsideClick = (event: MouseEvent | TouchEvent) => {
      const target = event.target;
      if (!(target instanceof Node)) return;
      if (conversationButtonRef.current?.contains(target)) return;
      if (chatSwitcherRef.current?.contains(target)) return;
      setChatSwitcherOpen(false);
    };

    document.addEventListener('mousedown', handleOutsideClick);
    document.addEventListener('touchstart', handleOutsideClick);

    return () => {
      document.removeEventListener('mousedown', handleOutsideClick);
      document.removeEventListener('touchstart', handleOutsideClick);
    };
  }, [open, chatSwitcherOpen]);

  const visibleChatGroups = useMemo(
    () => groupSessionsByCreatedAt(sessions),
    [sessions]
  );

  const visibleExpandedHistoryGroups = useMemo(
    () => groupSessionsByCreatedAt(sessions),
    [sessions]
  );

  // historyLoading now also covers initSession()'s own list fetch (see
  // where it's set there), not just the switcher's background refetches —
  // so it alone is enough to gate the switcher's loading state, whether or
  // not there was a stored session id to restore.
  const sessionsListLoading = historyLoading;
  const hasVisibleSessions = sessions.length > 0;

  const suggestedRows = useMemo(
    () => chunkItems(AI_CHAT_SUGGESTED_QUESTIONS),
    []
  );

  const mergeSessionsById = useCallback(
    (current: AIChatSession[], incoming: AIChatSession[]) => {
      const merged = new Map<string, AIChatSession>();

      current.forEach((session) => {
        merged.set(session.id, session);
      });

      incoming.forEach((session) => {
        const existing = merged.get(session.id);
        merged.set(
          session.id,
          existing ? { ...session, ...existing } : session
        );
      });

      return Array.from(merged.values()).sort((a, b) => {
        const aTime = a.createdAt ? Date.parse(a.createdAt) : 0;
        const bTime = b.createdAt ? Date.parse(b.createdAt) : 0;
        return bTime - aTime;
      });
    },
    []
  );

  const loadSessionHistory = useCallback(
    async ({
      search = historySearchRef.current,
      page = 1,
      append = false,
    }: {
      search?: string;
      page?: number;
      append?: boolean;
    } = {}) => {
      if (historyLoadingRef.current) {
        // Remember only the latest desired request -- once the in-flight
        // one finishes, that's the one that actually gets fetched, instead
        // of silently dropping it (which used to leave the list showing
        // results for an older search term if you kept typing).
        pendingHistoryRequestRef.current = { search, page, append };
        return;
      }

      historyLoadingRef.current = true;
      historySearchRef.current = search;

      const fetchAndApply = async () => {
        const res = await (
          useAgentCoreChatbot ? GetAgentCoreChatSessions : GetAIChatSessions
        )(undefined, {
          search: search.trim() || undefined,
          page,
          page_size: AI_CHAT_HISTORY_PAGE_SIZE,
        });
        const nextSessions = getSessionsFromPayload(res);
        const pageCount = getSessionsPageCountFromPayload(res);
        // page_count is recomputed fresh server-side on every request, so
        // it can drift between this page and the last one (a session
        // archived, or a draft chat gaining its first message and
        // re-entering the list) -- an empty page means nothing's left
        // regardless of what it claims.
        const hasMore =
          append && nextSessions.length === 0 ? false : page < pageCount;
        historyPageRef.current = page;
        historyHasMoreRef.current = hasMore;
        setHistoryHasMore(hasMore);
        lastHistoryFetchAtRef.current = Date.now();

        setSessions((prev) => {
          const merged = append
            ? mergeSessionsById(prev, nextSessions)
            : nextSessions;
          return sessionsListsEqual(prev, merged) ? prev : merged;
        });
      };

      try {
        if (!append && !search.trim()) {
          // A page-1, unfiltered fetch with no search term is always the
          // silent "confirm nothing changed" background check -- there's
          // already a valid list on screen (even an empty one), so no
          // duration of this is worth showing a loading state over. Only
          // the content itself updates, if something actually changed. An
          // active search or "load more" (below) are asking for content
          // that isn't already shown, so those still get the timed
          // indicator.
          await fetchAndApply();
        } else {
          await withDelayedLoadingIndicator(setHistoryLoading, fetchAndApply);
        }
      } catch (error: unknown) {
        if (isApiError(error)) {
          console.warn(
            `AI chat sessions load failed: ${
              error.response?.status
            } - ${customResponseErrorMessage(error) || error.message}`
          );
        }
      } finally {
        historyLoadingRef.current = false;
        const pending = pendingHistoryRequestRef.current;
        pendingHistoryRequestRef.current = null;
        if (pending) {
          const _ = loadSessionHistory(pending);
        }
      }
    },
    [mergeSessionsById, useAgentCoreChatbot]
  );

  const handleHistoryScroll = useCallback(
    (event: React.UIEvent<HTMLElement>) => {
      const target = event.currentTarget;
      const remaining =
        target.scrollHeight - target.scrollTop - target.clientHeight;

      if (
        remaining <= AI_CHAT_HISTORY_SCROLL_THRESHOLD &&
        historyHasMoreRef.current &&
        !historyLoadingRef.current
      ) {
        const _ = loadSessionHistory({
          page: historyPageRef.current + 1,
          append: true,
        });
      }
    },
    [loadSessionHistory]
  );

  const bumpSessionDataVersion = useCallback((sid: string) => {
    sessionDataVersionRef.current[sid] =
      (sessionDataVersionRef.current[sid] ?? 0) + 1;
  }, []);

  const handleSelectSession = useCallback(
    (session: AIChatSession) => {
      setChatSwitcherOpen(false);
      setExpandedSearchOpen(false);
      setActiveChatTitle(session.title);

      const hydrated = appendTypingIfPending(
        session.messages,
        session.id,
        pendingAssistantBySessionRef.current
      );
      setActiveSessionId(session.id);
      setMessages(hydrated);
      setActiveMessageActions({});
      setOpenQueryMessages({});
      clearCopiedActions();
      setTranscriptLoadingSessionId(hydrated.length === 0 ? session.id : null);

      selectSessionAbortRef.current?.abort();
      const ac = new AbortController();
      selectSessionAbortRef.current = ac;
      const versionAtFetchStart =
        sessionDataVersionRef.current[session.id] ?? 0;

      const _ = (async () => {
        try {
          const res = await (
            useAgentCoreChatbot ? GetAgentCoreChatSession : GetAIChatSession
          )(session.id, undefined, ac.signal);
          const payload = res?.data?.data?.data;
          const nextMessages = conversationHistoryToMessages(
            payload?.conversation_history ?? []
          );
          if (ac.signal.aborted) return;
          if (
            (sessionDataVersionRef.current[session.id] ?? 0) !==
            versionAtFetchStart
          ) {
            return;
          }
          const toShow = appendTypingIfPending(
            nextMessages,
            session.id,
            pendingAssistantBySessionRef.current
          );
          setSessions((prev) =>
            prev.map((item) =>
              item.id === session.id ? { ...item, messages: toShow } : item
            )
          );
          if (sessionIdRef.current === session.id) setMessages(toShow);
        } catch (error: unknown) {
          if (isRequestCancelled(error)) return;
          if (isApiError(error) && error.code === 'ERR_CANCELED') return;
          if (ac.signal.aborted) return;
          if (sessionIdRef.current !== session.id) return;

          let errorMessage =
            'Could not load this conversation. It may no longer be available.';
          if (isApiError(error)) {
            if (error.response?.status === 404) {
              errorMessage =
                'This conversation could not be found. It may have been deleted.';
            } else if (error.response?.status === 403) {
              errorMessage =
                'You do not have permission to view this conversation.';
            } else if (!error.response) {
              errorMessage =
                'Network error loading conversation. Please check your connection.';
            } else if (error.response?.status >= 500) {
              errorMessage = 'Server error. Please try again later.';
            } else {
              errorMessage = customResponseErrorMessage(error) || errorMessage;
            }
          }
          setMessages([{ type: 'system', content: errorMessage }]);
        } finally {
          setTranscriptLoadingSessionId((prev) =>
            prev === session.id ? null : prev
          );
        }
      })();
    },
    [clearCopiedActions, setActiveSessionId, useAgentCoreChatbot]
  );

  const selectSessionById = useCallback(
    (sid: string) => {
      const chatSession = sessions.find((item) => item.id === sid);
      if (chatSession) handleSelectSession(chatSession);
    },
    [handleSelectSession, sessions]
  );

  const initSession = useCallback(
    async (options: { skipRestore?: boolean } = {}) => {
      const { skipRestore = false } = options;
      setMessages([]);
      setActiveMessageActions({});
      setOpenQueryMessages({});
      clearCopiedActions();
      try {
        let fromServer: AIChatSession[] = [];

        // skipRestore is for callers that already know the answer (e.g.
        // "New chat" -- there is definitively nothing to restore). A
        // stored session id is the only other source of real uncertainty:
        // without one (a genuinely new tab, or a reload that never had
        // one), we already know -- synchronously, before any request --
        // that there's nothing to restore, so there's no reason to show a
        // loading state while the list fetch and session creation happen
        // in the background. Only gate the UI on network completion when
        // there's actually something stored to check the fetched list
        // against.
        const preferredExistingSessionId = skipRestore
          ? null
          : getStoredLastSessionId();

        if (!skipRestore) {
          if (preferredExistingSessionId) setDeterminingSession(true);

          // determiningSession deliberately stays false here when there's
          // nothing stored (see its own comment) -- but the switcher's
          // dropdown still has no real data yet either way, so it needs
          // its own signal to show a loading state instead of a premature
          // "No chats found" if opened during this window.
          //
          // historyLoadingRef (not just the state) must be set too --
          // loadSessionHistory's own guard checks the ref, and without
          // this, opening the switcher (or typing a search) while this
          // fetch is still in flight would fire a second, concurrent
          // fetch; whichever resolved last would silently win the
          // setSessions(...) call over the other's results.
          // Shown immediately, not through the delayed indicator -- this is
          // the very first check ever for this tab, so there's no
          // established "empty" state yet to protect from a flash. Skipping
          // straight to "No chats found" for the first ~150ms would be a
          // worse flicker than just showing the loading state right away.
          historyLoadingRef.current = true;
          setHistoryLoading(true);
          let listOutcome: Awaited<
            ReturnType<typeof GetAIChatSessions>
          > | null = null;
          try {
            listOutcome = await (
              useAgentCoreChatbot ? GetAgentCoreChatSessions : GetAIChatSessions
            )(undefined, {
              page: 1,
              page_size: AI_CHAT_HISTORY_PAGE_SIZE,
            });
          } catch {
            listOutcome = null;
          } finally {
            historyLoadingRef.current = false;
            setHistoryLoading(false);
          }

          if (listOutcome) {
            fromServer = getSessionsFromPayload(listOutcome);
            const pageCount = getSessionsPageCountFromPayload(listOutcome);
            historyPageRef.current = 1;
            historyHasMoreRef.current = pageCount > 1;
            setHistoryHasMore(pageCount > 1);
            lastHistoryFetchAtRef.current = Date.now();
          }

          // Reopening the drawer in a tab that already has a session
          // (even after closing it) should return to that session, not
          // spawn a new one server-side just to immediately abandon it in
          // favor of the restore below -- only start fresh once we know
          // there's nothing to restore.
          const sessionToRestore = preferredExistingSessionId
            ? fromServer.find(
                (session) => session.id === preferredExistingSessionId
              )
            : undefined;

          // A stored id absent from this list is NOT proof it's gone --
          // the backend list endpoint deliberately excludes sessions with
          // empty conversation_history (see AgentCoreChatSessionListAPIView
          // / list_company_chat_sessions), so a session that was started
          // but never sent a message will never show up here even though
          // it's still perfectly restorable. Always attempt the restore by
          // id when one is stored; the per-session fetch inside
          // handleSelectSession (which has no such empty-history filter)
          // is what actually confirms existence -- a real 404 surfaces
          // there as an error message, not as a silently-spawned new
          // session.
          if (preferredExistingSessionId) {
            const placeholder: AIChatSession = sessionToRestore ?? {
              id: preferredExistingSessionId,
              title: 'New chat',
              createdAt: new Date().toISOString(),
              messages: [],
            };
            // Both backends omit empty conversations from their session
            // list, so a still-unsent restored session deliberately isn't
            // added to `sessions` here either -- the switcher's dropdown
            // should mirror what's actually persisted, not what's merely
            // active locally. handleSelectSession works fine with a
            // session object that isn't (yet) in `sessions`.
            setSessions(fromServer);
            setDeterminingSession(false);
            handleSelectSession(placeholder);
            return;
          }

          // Decided: nothing to restore -- nothing was stored to begin
          // with. Reveal the welcome screen now; don't make it wait on
          // the session-creation call below, which only needs to finish
          // before a message can be *sent*.
          setSessions(fromServer);
          setDeterminingSession(false);
        }

        let startPayload: { session_id?: string } | undefined;
        let startError: string | null = null;

        try {
          const startRes = await (useAgentCoreChatbot
            ? StartAgentCoreChatSession()
            : StartAIChatSession());
          startPayload = startRes?.data?.data?.data;
        } catch (error: unknown) {
          startError = isApiError(error)
            ? customResponseErrorMessage(error) ||
              'Failed to start a new chat session'
            : 'Network error. Failed to start a new chat session';
        }

        if (!startPayload?.session_id) {
          setMessages([
            {
              type: 'system',
              content:
                startError || 'Could not start a new chat. Please try again.',
            },
          ]);
          return;
        }

        // A brand-new session has no messages yet, and both backends omit
        // empty conversations from their session list — it deliberately
        // isn't added to `sessions` here either, so the switcher's dropdown
        // always mirrors what's actually persisted. sendMessage() adds an
        // entry (with the real title, never "New chat") the moment the
        // first message is sent, which is the point it should start
        // showing up.
        setActiveSessionId(startPayload.session_id);
        setActiveChatTitle('New chat');
      } catch (error: unknown) {
        setMessages([
          {
            type: 'system',
            content:
              error instanceof Error
                ? `Error: ${error.message}`
                : 'An unexpected error occurred. Please try again.',
          },
        ]);
      } finally {
        setDeterminingSession(false);
      }
    },
    [
      clearCopiedActions,
      handleSelectSession,
      setActiveSessionId,
      useAgentCoreChatbot,
    ]
  );

  useEffect(() => {
    if (open && !sessionInitialisedRef.current) {
      setActiveSessionId(null);
      setSessions([]);
      setMessages([]);
      setActiveMessageActions({});
      setOpenQueryMessages({});
      clearCopiedActions();
      setQuestion('');
      setActiveChatTitle('New chat');
      sessionInitialisedRef.current = true;
      const _ = initSession();
    }
  }, [clearCopiedActions, open, initSession, setActiveSessionId]);

  const scheduleHistoryFetch = useCallback(
    (search: string) => {
      historyPageRef.current = 1;
      const _ = loadSessionHistory({ search, page: 1, append: false });
    },
    [loadSessionHistory]
  );

  useEffect(() => {
    if (!open || !chatSwitcherOpen) return;

    const stale =
      Date.now() - lastHistoryFetchAtRef.current > HISTORY_STALE_AFTER_MS;
    const timeoutId = window.setTimeout(
      () => scheduleHistoryFetch(chatSearch),
      stale ? 0 : 250
    );

    return () => window.clearTimeout(timeoutId);
  }, [chatSearch, chatSwitcherOpen, open, scheduleHistoryFetch]);

  useEffect(() => {
    if (!open || !isExpanded || !expandedSearchOpen) return;

    const stale =
      Date.now() - lastHistoryFetchAtRef.current > HISTORY_STALE_AFTER_MS;
    const timeoutId = window.setTimeout(
      () => scheduleHistoryFetch(expandedSearch),
      stale ? 0 : 250
    );

    return () => window.clearTimeout(timeoutId);
  }, [
    expandedSearch,
    expandedSearchOpen,
    isExpanded,
    scheduleHistoryFetch,
    open,
  ]);

  // `answers` is set only when submitting the question panel -- nextQuestion
  // is then its readable summary, which becomes the user's bubble.
  const sendMessage = async (
    nextQuestion = question,
    answers?: AskUserAnswers
  ) => {
    const trimmed = nextQuestion.trim();
    if (!trimmed || !sessionId || currentSessionAwaitingReply) return;

    const outgoingSessionId = sessionId;
    setOpenAskUser(null);
    const userMessage: AIChatMessage = answers
      ? { type: 'user', content: trimmed, answers }
      : { type: 'user', content: trimmed };
    const previousMessages = [...messages, userMessage];
    const withTyping: AIChatMessage[] = [
      ...previousMessages,
      { type: 'system', content: 'typing' },
    ];

    bumpSessionDataVersion(outgoingSessionId);
    setPendingAssistantBySession((prev) =>
      new Set(prev).add(outgoingSessionId)
    );
    // A panel submit never came from the composer, so whatever's half-typed
    // there stays put.
    if (!answers) setQuestion('');
    setMessages(withTyping);

    if (messages.length === 0) {
      const title = formatConversationTitle(trimmed);
      setActiveChatTitle(title);
      setSessions((prev) => {
        const exists = prev.find((session) => session.id === outgoingSessionId);
        if (!exists) {
          return [
            {
              id: outgoingSessionId,
              title,
              createdAt: new Date().toISOString(),
              messages: [],
            },
            ...prev,
          ];
        }
        return prev.map((session) =>
          session.id === outgoingSessionId && session.title === 'New chat'
            ? { ...session, title }
            : session
        );
      });
    }

    setSessions((prev) =>
      prev.map((session) =>
        session.id === outgoingSessionId
          ? { ...session, messages: previousMessages }
          : session
      )
    );

    const applyResult = (nextMessages: AIChatMessage[]) => {
      setSessions((prev) =>
        prev.map((session) =>
          session.id === outgoingSessionId
            ? { ...session, messages: nextMessages }
            : session
        )
      );
      if (sessionIdRef.current === outgoingSessionId) {
        setMessages(nextMessages);
      }
    };

    try {
      if (useAgentCoreChatbot) {
        let assistantContent = '';
        // Built in true arrival order alongside assistantContent -- a text
        // event extends the last part if it's also text, a chart event
        // always starts a new one. assistantContent stays the full
        // concatenated text alone (feedback/copy/search still read it
        // directly), unaffected by chart events.
        const parts: AIChatMessagePart[] = [];
        const askUser: AskUserRequest[] = [];
        const queries: string[] = [];
        // All tool-call queries for this turn concatenated into one block —
        // reuses the same single "show query" panel/field the old chatbot
        // already has, rather than a new per-step UI.
        const joinedQueries = () =>
          queries.length > 0 ? queries.join('\n\n-- \n\n') : undefined;
        // Ephemeral per-turn thinking timeline (separate from the query/SQL
        // feature above) -- tool_start appends a pending step, tool_end
        // flips the oldest still-pending step of that name to completed.
        // Matching by name rather than a call id: the backend only forwards
        // the tool's name, not its call id, and two concurrent calls to the
        // same tool are rare enough that "oldest pending wins" is a fine
        // approximation.
        const thinkingSteps: AIChatThinkingStep[] = [];
        // Set by a help_source frame when the answer comes from help articles.
        let helpSource: HelpCenterAnswerSource | undefined;
        // The "Thinking" spinner is shown exactly when there's no answer
        // text yet — derived from assistantContent at render time rather
        // than tracked as separate mutable state, so it can never drift
        // out of sync with what's actually on screen (of the three stream
        // event types, only `text` ever changes assistantContent; `query`
        // only feeds a debug panel that starts collapsed, and `thought` has
        // no UI surface at all, so neither should move the spinner either
        // way — deriving it this way makes that automatic instead of
        // something every branch has to remember).
        const render = () => {
          applyResult([
            ...previousMessages,
            {
              type: 'assistant',
              content: assistantContent,
              parts: [...parts],
              askUser: askUser.length > 0 ? [...askUser] : undefined,
              sql: joinedQueries(),
              streaming: assistantContent.length === 0,
              complete: false,
              thinkingSteps: [...thinkingSteps],
              helpSource,
            },
          ]);
        };
        // Seeds the real assistant message immediately, before the network
        // round trip -- otherwise the generic "typing" placeholder set
        // above is what's on screen until the first stream event arrives,
        // and swapping a differently-shaped message in for it later is a
        // full remount (different React key), not a smooth continuation.
        // Rendering this empty shell up front means there's only ever one
        // message object for this turn, evolving in place.
        render();

        await StreamAgentCoreChatMessage(
          {
            session_id: outgoingSessionId,
            message: trimmed,
            ...(answers ? { answers } : {}),
          },
          (event) => {
            if (event.type === 'thought') {
              // The reasoning text itself still isn't shown -- only that a
              // reasoning step happened, as a generic entry in the
              // timeline. Arrives as one complete frame (no pending phase
              // to wait out, unlike a tool call).
              thinkingSteps.push({
                name: THOUGHT_STEP_NAME,
                status: 'completed',
              });
              render();
              return;
            }
            if (event.type === 'tool_start') {
              thinkingSteps.push({ name: event.text, status: 'pending' });
              render();
              return;
            }
            if (event.type === 'tool_end') {
              const step = thinkingSteps.find(
                (item) => item.name === event.text && item.status === 'pending'
              );
              if (step) step.status = 'completed';
              render();
              return;
            }
            if (event.type === 'query') {
              queries.push(event.text);
              render();
              return;
            }
            if (event.type === 'chart') {
              parts.push({ kind: 'chart', spec: event.spec });
              render();
              return;
            }
            if (event.type === 'help_source') {
              helpSource = event.source;
              render();
              return;
            }
            if (event.type === 'action_result') {
              parts.push({ kind: 'action_result', result: event.result });
              render();
              return;
            }
            if (event.type === 'ask_user') {
              askUser.push(event.request);
              setOpenAskUser({
                sessionId: outgoingSessionId,
                request: event.request,
              });
              render();
              return;
            }
            assistantContent += event.text;
            const lastPart = parts[parts.length - 1];
            if (lastPart && lastPart.kind === 'text') {
              lastPart.text += event.text;
            } else {
              parts.push({ kind: 'text', text: event.text });
            }
            render();
          }
        );

        // Keyed off actual answer text or a rendered chart, not "did any
        // event arrive" — a turn that only ever produced thought/query
        // events (no final answer text or chart, e.g. an empty final
        // generation) has nothing to show as a completed message either, so
        // it belongs in the same fallback as a turn that produced no events
        // at all.
        const hasAnswer =
          Boolean(assistantContent) || parts.length > 0 || askUser.length > 0;
        if (hasAnswer) {
          applyResult([
            ...previousMessages,
            {
              type: 'assistant',
              content: assistantContent,
              parts: [...parts],
              askUser: askUser.length > 0 ? [...askUser] : undefined,
              sql: joinedQueries(),
              complete: true,
              thinkingSteps: [...thinkingSteps],
              helpSource,
            },
          ]);
        }

        if (!hasAnswer) {
          applyResult([
            ...previousMessages,
            {
              type: 'system',
              content: 'Assistant: No response from assistant.',
            },
          ]);
        }
      } else {
        const res = await SendAIChatMessage({
          session_id: outgoingSessionId,
          message: trimmed,
        });
        const payload = res?.data?.data?.data;
        if (payload?.response) {
          applyResult([
            ...previousMessages,
            {
              type: 'assistant',
              content: payload.response,
              sql: normalizeSql(payload.sql),
              helpSource:
                payload.source === 'help_center'
                  ? toHelpSource(payload.label, payload.help_articles)
                  : undefined,
            },
          ]);
        } else {
          applyResult([
            ...previousMessages,
            {
              type: 'system',
              content: `Assistant: ${
                payload?.error ||
                payload?.message ||
                'No response from assistant.'
              }`,
            },
          ]);
        }
      }
    } catch (error: unknown) {
      let errorMessage = 'Failed to send message. Please try again.';
      if (isApiError(error)) {
        if (error.response?.status === 429) {
          errorMessage =
            'Too many requests. Please wait a moment before trying again.';
        } else if (error.response?.status === 403) {
          errorMessage = 'You do not have permission to use this feature.';
        } else if (error.response?.status === 400) {
          errorMessage =
            customResponseErrorMessage(error) ||
            'Invalid request. Please check your input.';
        } else if ((error.response?.status ?? 0) >= 500) {
          errorMessage = 'Server error. Please try again later.';
        } else if (!error.response) {
          errorMessage = 'Network error. Please check your connection.';
        } else {
          errorMessage = customResponseErrorMessage(error) || errorMessage;
        }
      } else if (error instanceof Error) {
        errorMessage = error.message;
      }
      applyResult([
        ...previousMessages,
        { type: 'system', content: `Error: ${errorMessage}` },
      ]);
    } finally {
      bumpSessionDataVersion(outgoingSessionId);
      setPendingAssistantBySession((prev) => {
        const next = new Set(prev);
        next.delete(outgoingSessionId);
        return next;
      });
    }
  };

  const handleNewChat = () => {
    // The drawer stays mounted (off-screen, aria-hidden) while closed for
    // the slide animation, so its controls are still technically reachable
    // via keyboard even then -- guard explicitly rather than relying only
    // on aria-hidden/CSS, since this one discards the active session.
    if (!open) return;
    selectSessionAbortRef.current?.abort();
    selectSessionAbortRef.current = null;
    clearStoredLastSessionId();
    setActiveChatTitle('New chat');
    setMessages([]);
    setActiveMessageActions({});
    setOpenQueryMessages({});
    clearCopiedActions();
    setActiveSessionId(null);
    setQuestion('');
    setTranscriptLoadingSessionId(null);
    setPendingAssistantBySession(new Set());
    setOpenAskUser(null);
    setChatSwitcherOpen(false);
    setChatSearch('');
    setExpandedSearch('');
    setExpandedSearchOpen(false);
    historySearchRef.current = '';
    historyPageRef.current = 1;
    historyHasMoreRef.current = false;
    setHistoryHasMore(false);
    // Already handled directly (initSession below) -- true, not false, so
    // a later close/reopen of this tab doesn't redo the restore-check and
    // treat this freshly-started session as if it needed restoring.
    sessionInitialisedRef.current = true;
    const _ = initSession({ skipRestore: true });
  };

  // Help center hand-off: sends a question from Help once, in a fresh chat.
  useInsightChatHandoff({
    open,
    pendingQuestion,
    onPendingQuestionSent,
    sessionId,
    sessionReady: !!sessionId && visibleTranscriptLoadingSessionId === null,
    hasMessages,
    awaitingReply: currentSessionAwaitingReply,
    startNewChat: handleNewChat,
    send: (value) => {
      const _ = sendMessage(value);
    },
  });

  const handleFeedback = async (
    messageIndex: number,
    rating: AIChatFeedbackRating
  ) => {
    if (!sessionId) return;
    const message = messages[messageIndex];
    if (!message || message.type !== 'assistant') return;
    const isUpdate = message.feedback != null;
    let userQuery = '';
    for (let index = messageIndex - 1; index >= 0; index--) {
      if (messages[index].type === 'user') {
        userQuery = messages[index].content;
        break;
      }
    }

    try {
      const payload = {
        session_id: sessionId,
        message_index: messageIndex,
        user_query: userQuery,
        assistant_response: message.content,
        rating,
        comment: '',
      };
      if (isUpdate) {
        await (
          useAgentCoreChatbot
            ? UpdateAgentCoreChatFeedback
            : UpdateAIChatFeedback
        )(payload);
      } else {
        await (
          useAgentCoreChatbot
            ? CreateAgentCoreChatFeedback
            : CreateAIChatFeedback
        )(payload);
      }
      setMessages((prev) =>
        prev.map((item, index) =>
          index === messageIndex ? { ...item, feedback: rating } : item
        )
      );
    } catch (error: unknown) {
      if (isApiError(error)) {
        console.warn(
          `Feedback save failed: ${
            error.response?.status
          } - ${customResponseErrorMessage(error) || error.message}`
        );
      } else if (error instanceof Error) {
        console.warn(`Feedback save failed: ${error.message}`);
      }
    }
  };

  const handleExpand = () => {
    // Same reasoning as handleNewChat above -- reachable via keyboard even
    // while closed, and setting isExpanded here would otherwise silently
    // persist (the close-reset effect only fires on the open->false
    // transition, not on every render while already closed) until the
    // drawer is next opened, which would then surprise-open already
    // expanded.
    if (!open) return;
    if (
      typeof window !== 'undefined' &&
      !window.matchMedia(EXPANDED_VIEW_QUERY).matches
    ) {
      return;
    }

    setIsExpanded(true);
    setChatSwitcherOpen(false);
    setExpandedSidebarCollapsed(false);
  };

  const handleCollapse = () => {
    setIsExpanded(false);
    setChatSwitcherOpen(false);
  };

  const handleExpandedSidebarToggle = () => {
    setExpandedSidebarCollapsed((prev) => {
      const next = !prev;
      if (next) {
        setExpandedSearchOpen(false);
        setExpandedSearch('');
      }
      return next;
    });
  };

  const handleComposerKeyDown = (
    event: React.KeyboardEvent<HTMLTextAreaElement>
  ) => {
    if (event.key === 'Enter' && !event.shiftKey) {
      event.preventDefault();
      const _ = sendMessage();
    }
  };

  const handleChartDrilldown = useCallback(
    (category: string, drilldown: ChatChartDrilldown) => {
      if (!sessionId) return;
      const { entity, filters } = drilldown;
      setNavDirection('forward');
      setChartDrilldown({
        category,
        entity,
        filters,
        loading: true,
        error: null,
        data: null,
      });
      GetAgentCoreChatDrilldown(sessionId, entity, filters)
        .then((res) => {
          const data = res?.data?.data?.data as
            AgentCoreChatDrilldownResult | undefined;
          setChartDrilldown((prev) =>
            prev && prev.category === category && prev.entity === entity
              ? { ...prev, loading: false, data: data ?? null }
              : prev
          );
        })
        .catch(() => {
          setChartDrilldown((prev) =>
            prev && prev.category === category && prev.entity === entity
              ? {
                  ...prev,
                  loading: false,
                  error: 'Unable to load these results. Please try again.',
                }
              : prev
          );
        });
    },
    [sessionId]
  );

  const handleChartDrilldownBack = useCallback(() => {
    setNavDirection('backward');
    setChartDrilldown(null);
  }, []);

  const handleSelectShipment = useCallback((shipmentId: string) => {
    setNavDirection('forward');
    setOpenShipmentDetailId(shipmentId);
  }, []);

  const handleShipmentDetailBack = useCallback(() => {
    setNavDirection('backward');
    setOpenShipmentDetailId(null);
  }, []);

  // Archive / mark-as-delivered inside the detail panel are real, live
  // mutations (not just display state) -- without this, the drilldown list
  // behind the panel would still show the shipment's old state if the user
  // backs out after one succeeds. Re-runs the same fetch handleChartDrilldown
  // already knows how to do, for whatever category/filters are current.
  const handleShipmentMutationSuccess = useCallback(() => {
    if (!chartDrilldown) return;
    handleChartDrilldown(chartDrilldown.category, {
      entity: chartDrilldown.entity,
      filters: chartDrilldown.filters,
    });
  }, [chartDrilldown, handleChartDrilldown]);

  // Minimal toggleDrawer: ShipmentDetailDrawerWithTeams was built for the
  // Shipments/Home pages' full drawer stack (Edit, return labels, etc.) --
  // here we support closing back to the drilldown list, navigating to
  // another shipment (e.g. a linked shipment click within the detail view
  // itself), and opening notifications/edit (both wired to real views
  // below). Every other action it can dispatch is still a no-op in chat.
  const handleShipmentDetailToggle = useCallback(
    (action: string | boolean, type?: string, id?: string) => {
      if (action === false || action === 'close') {
        setNavDirection('backward');
        setOpenShipmentDetailId(null);
        return;
      }
      if (action === 'open' && type === 'Detail' && id) {
        setNavDirection('forward');
        setOpenShipmentDetailId(id);
        return;
      }
      if (action === 'open' && type === 'notifications' && id) {
        setNavDirection('forward');
        setNotificationsShipmentId(id);
        return;
      }
      if (action === 'open' && type === 'Edit' && id) {
        setNavDirection('forward');
        setIsEditingShipment(true);
      }
    },
    []
  );

  // ShipmentNotificationDrawer's own toggleDrawer contract: 'open'/'Detail'
  // is its back-to-detail arrow, false/'all' is its close-everything action
  // (see ShipmentNotificationHeader.tsx) -- both handled here rather than
  // adding our own back button on top of its existing header.
  const handleNotificationsToggle = useCallback(
    (action: string | boolean, type?: string, id?: string) => {
      if (action === 'open' && type === 'Detail' && id) {
        setNavDirection('backward');
        setNotificationsShipmentId(null);
        setOpenShipmentDetailId(id);
        return;
      }
      if (action === false) {
        setNavDirection('backward');
        setNotificationsShipmentId(null);
        if (type === 'all') {
          setOpenShipmentDetailId(null);
        }
      }
    },
    []
  );

  // EditShipmentDrawerWithTeams's own toggleDrawer contract (see
  // HomeChartShipmentsList.tsx's identical adapter): `toggleDrawer(true,
  // 'Detail')` is its back-to-detail arrow, `toggleDrawer(false)` is its
  // close button. Both just mean "back to the detail view" here -- unlike
  // Home's page-level drawer stack, chat has nothing above Detail for
  // "close" to reveal instead.
  const handleEditShipmentToggle = useCallback(() => {
    setNavDirection('backward');
    setIsEditingShipment(false);
  }, []);

  const activeAskUser =
    openAskUser && openAskUser.sessionId === sessionId
      ? openAskUser.request
      : null;
  // Only once the turn that raised it has finished streaming -- Continue
  // sends a message, which is blocked until then anyway.
  const questionPanel =
    activeAskUser && !currentSessionAwaitingReply ? (
      <AIChatQuestionPanel
        key={activeAskUser.id}
        request={activeAskUser}
        onSubmit={(content) => {
          const _ = sendMessage(
            summarizeAskUserAnswers(activeAskUser, content),
            {
              id: activeAskUser.id,
              action: 'accept',
              content,
            }
          );
        }}
      />
    ) : null;

  // Links from results stay inside the app, and the page must be visible
  // behind the chat, not covered by it.
  const openChatLink = onOpenLink
    ? (path: string) => {
        if (!isInAppPath(path)) return;
        setIsExpanded(false);
        onOpenLink(path);
      }
    : undefined;

  const chatThread = (
    <AIChatThread
      activeAskUserId={activeAskUser?.id ?? null}
      trailing={questionPanel}
      activeMessageActions={activeMessageActions}
      copiedActionKeys={copiedActionKeys}
      hasMessages={hasMessages}
      messages={messages}
      openQueryMessages={openQueryMessages}
      threadRef={threadRef}
      transcriptLoadingSessionId={visibleTranscriptLoadingSessionId}
      copyText={copyText}
      onChartDrilldown={handleChartDrilldown}
      onFeedback={handleFeedback}
      onOpenLink={openChatLink}
      setActiveMessageActions={setActiveMessageActions}
      setOpenQueryMessages={setOpenQueryMessages}
    />
  );

  // Shared between the docked and full-screen mains (below) so the
  // notifications/shipment-detail/chart-drilldown views -- and everything
  // they reuse (ShipmentDetailDrawerWithTeams, ShipmentHeader,
  // ShipmentNotificationDrawer) -- are defined exactly once. `null` means
  // neither view is active, so each main falls back to its own (already
  // distinct) chat/empty-state layout.
  const drilldownContent = notificationsShipmentId ? (
    <div
      key={notificationsShipmentId}
      className={`ai-chat-drawer__shipment-notifications ${viewEnterClass}`}
    >
      <ShipmentNotificationDrawer
        allPermissions={shipmentMenuPermissions}
        shipmentId={notificationsShipmentId}
        toggleDrawer={handleNotificationsToggle}
      />
    </div>
  ) : isEditingShipment && openShipmentDetailId ? (
    // EditShipmentDrawerWithTeams renders its own complete header (back
    // arrow, "Edit shipment" title, close button) -- same reasoning as
    // ShipmentNotificationDrawer above, no extra header added on top of it.
    <div
      key={`edit-${openShipmentDetailId}`}
      className={`ai-chat-drawer__shipment-detail ${viewEnterClass}`}
    >
      <EditShipmentDrawerWithTeams
        shipmentId={openShipmentDetailId}
        toggleDrawer={handleEditShipmentToggle}
        onEditShipmentSuccess={() => handleShipmentMutationSuccess()}
      />
    </div>
  ) : openShipmentDetailId ? (
    <div
      key={openShipmentDetailId}
      className={`ai-chat-drawer__shipment-detail ${viewEnterClass}`}
    >
      <div className='ai-chat-drawer__drilldown-header'>
        <button
          type='button'
          className='ai-chat-drawer__drilldown-back-btn'
          onClick={handleShipmentDetailBack}
          aria-label='Back to list'
        >
          <AIChatChevronLeftIcon width={16} height={16} color='currentColor' />
        </button>
        <span className='ai-chat-drawer__drilldown-title'>
          {chartDrilldown?.category ?? 'List'}
        </span>
      </div>
      <GoogleMapsProvider>
        {/* isFromHome intentionally omitted (defaults false): it only
          controls ShipmentHeader's own internal back arrow (plus a
          marginBottom that's '0px' either way) -- true would duplicate the
          labeled back button above, which is the one we actually want. */}
        <ShipmentDetailDrawerWithTeams
          isOpen={true}
          openShipmentId={openShipmentDetailId}
          toggleDrawer={handleShipmentDetailToggle}
          shipmentSuccess={handleShipmentMutationSuccess}
          filterByLinkCode={() => {}}
          onGenerateReturnLabel={(shipment) => setReturnLabelShipment(shipment)}
        />
      </GoogleMapsProvider>
    </div>
  ) : chartDrilldown ? (
    <AIChatChartDrilldown
      category={chartDrilldown.category}
      loading={chartDrilldown.loading}
      error={chartDrilldown.error}
      data={chartDrilldown.data}
      onBack={handleChartDrilldownBack}
      onSelectShipment={handleSelectShipment}
      viewEnterClassName={viewEnterClass}
    />
  ) : null;

  return createPortal(
    // AntD popups (Dropdown/Modal/Tooltip/Select, etc.) size their z-index off
    // the app-wide zIndexPopupBase (1100, see antdTheme.ts) -- well under this
    // drawer's own 1600/1700 shell z-index, so anything like ShipmentHeader's
    // "..." menu or its notification/geo-fence modals rendered under it. This
    // nested ConfigProvider only raises the baseline for popups triggered
    // from inside the drawer, leaving the rest of the app's 1100 untouched.
    <ConfigProvider theme={{ token: { zIndexPopupBase: 1800 } }}>
      <div
        className={`ai-chat-drawer-shell ${
          isExpanded ? 'ai-chat-drawer-shell--expanded' : ''
        }`}
        role='presentation'
      >
        <aside
          className={`ai-chat-drawer ${open ? 'open' : ''} ${
            isExpanded ? 'ai-chat-drawer--expanded' : ''
          }`}
          aria-label='AI Shipment Assistant'
          role='dialog'
          aria-modal='false'
          aria-hidden={!open}
        >
          {isExpanded ? (
            <>
              <button
                type='button'
                className='ai-chat-drawer__expanded-close'
                onClick={onClose}
                aria-label='Close AI assistant'
              >
                <CloseDrawerIcon />
              </button>

              <div className='ai-chat-drawer__expanded-layout'>
                <aside
                  className={`ai-chat-drawer__expanded-sidebar ${
                    expandedSidebarCollapsed
                      ? 'ai-chat-drawer__expanded-sidebar--collapsed'
                      : ''
                  }`}
                  aria-label='Chat history'
                >
                  <div className='ai-chat-drawer__expanded-sidebar-header'>
                    <button
                      type='button'
                      className='ai-chat-drawer__icon-btn ai-chat-drawer__expanded-sidebar-toggle'
                      onClick={handleExpandedSidebarToggle}
                      aria-label={
                        expandedSidebarCollapsed
                          ? 'Expand chat history'
                          : 'Collapse chat history'
                      }
                      aria-expanded={!expandedSidebarCollapsed}
                    >
                      <AIChatChevronLeftIcon />
                    </button>
                    {!expandedSidebarCollapsed && (
                      <div className='ai-chat-drawer__expanded-sidebar-actions'>
                        <button
                          type='button'
                          className='ai-chat-drawer__icon-btn'
                          onClick={() => setExpandedSearchOpen((prev) => !prev)}
                          aria-label='Search chats'
                        >
                          <AIChatSearchIcon />
                        </button>
                        <button
                          type='button'
                          className='ai-chat-drawer__icon-btn'
                          onClick={handleNewChat}
                          aria-label='New chat'
                        >
                          <AIChatEditSquareIcon />
                        </button>
                      </div>
                    )}
                  </div>

                  {!expandedSidebarCollapsed && (
                    <div
                      className='ai-chat-drawer__expanded-history'
                      onScroll={handleHistoryScroll}
                    >
                      {expandedSearchOpen && (
                        <AIChatHistorySearch
                          value={expandedSearch}
                          onChange={setExpandedSearch}
                          className='ai-chat-drawer__expanded-search'
                        />
                      )}
                      <div className='ai-chat-drawer__expanded-history-title'>
                        Chat history
                      </div>
                      <AIChatHistoryGroups
                        groups={visibleExpandedHistoryGroups}
                        activeSessionId={sessionId}
                        loading={sessionsListLoading}
                        itemClassName='ai-chat-drawer__expanded-history-item'
                        groupClassName='ai-chat-drawer__expanded-history-group'
                        labelClassName='ai-chat-drawer__expanded-history-label'
                        emptyClassName='ai-chat-drawer__expanded-history-empty'
                        onSelectSession={selectSessionById}
                      />
                      {historyLoading && hasVisibleSessions && (
                        <div className='ai-chat-drawer__history-status'>
                          Loading chats...
                        </div>
                      )}
                      {!historyLoading && historyHasMore && (
                        <div className='ai-chat-drawer__history-status'>
                          Scroll for more
                        </div>
                      )}
                    </div>
                  )}
                </aside>

                <main
                  className={`ai-chat-drawer__expanded-main ${
                    drilldownContent
                      ? 'ai-chat-drawer__expanded-main--drilldown'
                      : hasActiveConversation
                        ? 'ai-chat-drawer__expanded-main--chat'
                        : ''
                  }`}
                  ref={bodyRef}
                >
                  {drilldownContent ??
                    (hasActiveConversation ? (
                      <div className='ai-chat-drawer__chat-screen ai-chat-drawer__chat-screen--expanded'>
                        {chatThread}

                        <div className='ai-chat-drawer__chat-footer ai-chat-drawer__chat-footer--expanded'>
                          <AIChatComposer
                            textareaRef={textareaRef}
                            value={question}
                            onChange={setQuestion}
                            onKeyDown={handleComposerKeyDown}
                            onSave={toggleSaveQuestion}
                            onSend={() => {
                              const _ = sendMessage();
                            }}
                            isSaved={isCurrentQuestionSaved}
                            saveDisabled={!question.trim()}
                            sendDisabled={sendDisabled}
                            ariaLabel='Ask a follow up'
                            placeholder='Ask a follow up...'
                            disabled={currentSessionAwaitingReply}
                            expanded
                            followup
                          />
                          <p className='ai-chat-drawer__disclaimer'>
                            The AI Shipment Assistant can make mistakes. Verify
                            important information.
                          </p>
                        </div>
                      </div>
                    ) : (
                      <div className='ai-chat-drawer__expanded-empty'>
                        <section className='ai-chat-drawer__intro ai-chat-drawer__intro--expanded'>
                          <h2>What are you interested in?</h2>
                          <p>
                            Unlock key insights and powerful data so you can
                            better manage your supply chain
                          </p>
                        </section>

                        <AIChatComposer
                          textareaRef={textareaRef}
                          value={question}
                          onChange={setQuestion}
                          onKeyDown={handleComposerKeyDown}
                          onSave={toggleSaveQuestion}
                          onSend={() => {
                            const _ = sendMessage();
                          }}
                          isSaved={isCurrentQuestionSaved}
                          saveDisabled={!question.trim()}
                          sendDisabled={sendDisabled}
                          ariaLabel='Ask AI'
                          expanded
                        />

                        <AIChatSavedQuestionsSection
                          savedQuestions={savedQuestions}
                          rows={expandedSavedRows}
                          showAll={showAllExpandedSaved}
                          savingQuestion={savingQuestion}
                          expanded
                          onSendQuestion={(value) => {
                            const _ = sendMessage(value);
                          }}
                          onRemoveQuestion={(value) => {
                            const _ = removeSavedQuestion(value);
                          }}
                          onToggleShowAll={() =>
                            setShowAllExpandedSaved((prev) => !prev)
                          }
                        />

                        <AIChatSuggestedQuestionsSection
                          rows={suggestedRows}
                          expanded
                          onSendQuestion={(value) => {
                            const _ = sendMessage(value);
                          }}
                        />
                      </div>
                    ))}
                </main>
              </div>

              <button
                type='button'
                className='ai-chat-drawer__expanded-collapse'
                onClick={handleCollapse}
                aria-label='Collapse AI assistant'
              >
                <AIChatCollapseIcon />
              </button>
            </>
          ) : (
            <>
              <header className='ai-chat-drawer__header'>
                <button
                  ref={conversationButtonRef}
                  type='button'
                  className='ai-chat-drawer__conversation'
                  aria-expanded={chatSwitcherOpen}
                  aria-label='Select past conversations'
                  onClick={() => setChatSwitcherOpen((prev) => !prev)}
                >
                  <EllipsisTooltip
                    // activeChatTitle still holds its 'New chat' default
                    // while determiningSession is true -- we don't yet know
                    // if that's actually right (we might be about to
                    // restore a session with a real title), so blank it
                    // rather than show a value that may be about to change
                    // out from under it.
                    text={determiningSession ? '' : activeChatTitle}
                    className='ai-chat-drawer__conversation-title'
                    zIndex={2200}
                  />
                  <AIChatExpandMoreIcon />
                </button>

                <div className='ai-chat-drawer__header-actions'>
                  <button
                    type='button'
                    className='ai-chat-drawer__icon-btn'
                    onClick={handleNewChat}
                    aria-label='New chat'
                  >
                    <AIChatEditSquareIcon />
                  </button>
                  <button
                    type='button'
                    className='ai-chat-drawer__icon-btn'
                    onClick={handleExpand}
                    aria-label='Expand AI assistant'
                  >
                    <AIChatExpandIcon />
                  </button>
                  <button
                    type='button'
                    className='ai-chat-drawer__icon-btn'
                    onClick={onClose}
                    aria-label='Close AI assistant'
                  >
                    <CloseDrawerIcon />
                  </button>
                </div>

                {chatSwitcherOpen && (
                  <div
                    ref={chatSwitcherRef}
                    className='ai-chat-drawer__switcher'
                  >
                    <AIChatHistorySearch
                      value={chatSearch}
                      onChange={setChatSearch}
                      className='ai-chat-drawer__switcher-search'
                    />
                    <div
                      className='ai-chat-drawer__switcher-list'
                      onScroll={handleHistoryScroll}
                    >
                      <AIChatHistoryGroups
                        groups={visibleChatGroups}
                        activeSessionId={sessionId}
                        loading={sessionsListLoading}
                        itemClassName='ai-chat-drawer__switcher-item'
                        groupClassName='ai-chat-drawer__switcher-group'
                        labelClassName='ai-chat-drawer__switcher-label'
                        emptyClassName='ai-chat-drawer__switcher-empty'
                        onSelectSession={selectSessionById}
                      />
                      {historyLoading && hasVisibleSessions && (
                        <div className='ai-chat-drawer__history-status'>
                          Loading chats...
                        </div>
                      )}
                      {!historyLoading && historyHasMore && (
                        <div className='ai-chat-drawer__history-status'>
                          Scroll for more
                        </div>
                      )}
                    </div>
                  </div>
                )}
              </header>

              <main className='ai-chat-drawer__body' ref={bodyRef}>
                {drilldownContent ??
                  (hasActiveConversation ? (
                    <div
                      className={`ai-chat-drawer__chat-screen ${viewEnterClass}`}
                    >
                      {chatThread}

                      <div className='ai-chat-drawer__chat-footer'>
                        <AIChatComposer
                          textareaRef={textareaRef}
                          value={question}
                          onChange={setQuestion}
                          onKeyDown={handleComposerKeyDown}
                          onSave={toggleSaveQuestion}
                          onSend={() => {
                            const _ = sendMessage();
                          }}
                          isSaved={isCurrentQuestionSaved}
                          saveDisabled={!question.trim()}
                          sendDisabled={sendDisabled}
                          ariaLabel='Ask a follow up'
                          placeholder='Ask a follow up...'
                          disabled={currentSessionAwaitingReply}
                          followup
                        />
                        <p className='ai-chat-drawer__disclaimer'>
                          The AI Shipment Assistant can make mistakes. Verify
                          important information.
                        </p>
                      </div>
                    </div>
                  ) : (
                    <>
                      <section className='ai-chat-drawer__intro'>
                        <h2>What are you interested in?</h2>
                        <p>
                          Unlock key insights and powerful data so you can
                          better manage your supply chain
                        </p>
                      </section>

                      <AIChatComposer
                        textareaRef={textareaRef}
                        value={question}
                        onChange={setQuestion}
                        onKeyDown={handleComposerKeyDown}
                        onSave={toggleSaveQuestion}
                        onSend={() => {
                          const _ = sendMessage();
                        }}
                        isSaved={isCurrentQuestionSaved}
                        saveDisabled={!question.trim()}
                        sendDisabled={sendDisabled}
                        ariaLabel='Ask AI'
                      />

                      <AIChatSavedQuestionsSection
                        savedQuestions={savedQuestions}
                        rows={visibleSavedRows}
                        showAll={showAllSaved}
                        savingQuestion={savingQuestion}
                        onSendQuestion={(value) => {
                          const _ = sendMessage(value);
                        }}
                        onRemoveQuestion={(value) => {
                          const _ = removeSavedQuestion(value);
                        }}
                        onToggleShowAll={() => setShowAllSaved((prev) => !prev)}
                      />

                      <AIChatSuggestedQuestionsSection
                        rows={suggestedRows}
                        onSendQuestion={(value) => {
                          const _ = sendMessage(value);
                        }}
                      />
                    </>
                  ))}
              </main>
            </>
          )}
        </aside>
      </div>

      {/* Generate return label opens as a floating overlay on top of
          whatever's currently showing, matching NewShipments.tsx's own
          handleGenerateReturnLabel -- not swapped into drilldownContent the
          way Edit is, since the real page renders it the same detached way. */}
      {returnLabelShipment && (
        <CommonDrawer
          open={true}
          onClose={() => setReturnLabelShipment(null)}
          width={520}
        >
          <QuoteShipmentDrawer
            mode='return'
            returnSourceShipment={returnLabelShipment}
            onClose={() => setReturnLabelShipment(null)}
          />
        </CommonDrawer>
      )}
    </ConfigProvider>,
    document.body
  );
};

export default AIChatDrawer;
export type * from './types';
